import React from 'react'
import { Box, Accordion, AccordionDetails } from '@mui/material'
import { SelectCard } from 'src/Components/Ui/SelectCard'
import { TabPanel } from 'src/Components/Ui/TabPanel'
import { State, PartialAction, Position } from 'src/types'
import { EmbroideryFormUpDown } from 'src/Components/Domain/EmbroideryFormUpDown'
import { EmbroideryAccordionSummary } from 'src/Components/Domain/EmbroideryAccordionSummary'
import { EmbroideryContent } from 'src/Components/Domain/EmbroideryContent'
import {
  positionObjs as positionObjects,
  colorObjs,
  shadowEdgeColorObjs,
  FIRST_MITT_POSITION_OPTIONS
} from 'src/features/gorilla/constants/embroidery'
import { characterCheckHelper, selectableChecker, useGorillaEmbroideriesDispatchGenerator } from 'src/features/gorilla/logic/embroiderySetting'
import { positionChecker } from 'src/logic'
import { embroideryFlagGenerator, selectablePositionGenerator } from 'src/logic'
import { generateSubColors } from 'src/logic'

type Props = {
  state: State
  selectedIndex: number
  position: Position
  dispatch: React.Dispatch<PartialAction>
}

export const EmbroiderySetter: React.FC<Props> = ({ state, selectedIndex, position, dispatch }) => {
  const { series, embroideries } = state
  const { handle } = useGorillaEmbroideriesDispatchGenerator(dispatch, embroideries)
  const isSelectable = selectableChecker(series, position)
  const { isFirst } = positionChecker(position)

  const positionOptions = isFirst ? FIRST_MITT_POSITION_OPTIONS : positionObjects
  return (
    <TabPanel selectedIndex={selectedIndex} index={2}>
      {isSelectable && (
        <>
          <EmbroideryFormUpDown {...{ embroideries, dispatch }} positionObjects={positionOptions} />
          {embroideries.map((embroidery, i) => {
            const { shadowColorLabel, edgeColorLabel, disabledShadowColor, disabledEdgeColor, isSelectedTypeFace } =
              embroideryFlagGenerator(embroidery)
            const { contentMaxLength, existsContent, typeFaceObjects } = characterCheckHelper(embroidery.content)
            const selectablePosition = selectablePositionGenerator(embroideries, positionOptions, [], false, i)
            const shadowColors = generateSubColors(disabledShadowColor, shadowEdgeColorObjs)
            const edgeColors = generateSubColors(disabledEdgeColor, shadowEdgeColorObjs)

            return (
              <Box key={`${embroidery.id}-${i}`} my={1}>
                <Accordion defaultExpanded>
                  <EmbroideryAccordionSummary embroideryIndex={i} />
                  <AccordionDetails style={{ flexWrap: 'wrap' }}>
                    <Box width={'100%'}>
                      <SelectCard
                        summary={'位置'}
                        selectedLabel={embroidery.position?.label}
                        objects={selectablePosition}
                        index={i}
                        handleChange={handle.position}
                        disabled={existsContent}
                        caution={existsContent && '刺繍位置を変更するには、刺繍内容をリセットしてください'}
                      />
                    </Box>
                    <Box width={'100%'}>
                      <EmbroideryContent
                        content={embroidery.content}
                        contentMaxLength={contentMaxLength}
                        embroideryIndex={i}
                        handleContent={handle.content}
                      />
                    </Box>
                    <Box width={'100%'} mt={2}>
                      <SelectCard
                        summary={'書体'}
                        selectedLabel={embroidery.typeFace?.label}
                        objects={typeFaceObjects}
                        index={i}
                        handleChange={handle.typeFace}
                        disabled={!existsContent}
                        description={'書体は一部、実際の書体と異なります。カタログを併せてご確認ください'}
                      />
                    </Box>
                    <Box width={'100%'}>
                      <SelectCard
                        summary={'刺繍カラー'}
                        selectedLabel={embroidery.color?.label}
                        selectedColor={embroidery.color?.color}
                        objects={colorObjs}
                        isError={embroidery.color?.value === 'unselected'}
                        index={i}
                        handleChange={handle.color}
                        disabled={!isSelectedTypeFace}
                        caution={!isSelectedTypeFace && '書体を選択してください'}
                      />
                    </Box>
                    <Box width={'100%'}>
                      <SelectCard
                        summary={'影カラー'}
                        selectedLabel={shadowColorLabel}
                        selectedColor={embroidery.shadowColor?.color}
                        objects={shadowColors}
                        isError={embroidery.shadowColor?.value === 'unselected'}
                        defaultExpanded={false}
                        index={i}
                        handleChange={handle.shadowColor}
                      />
                    </Box>
                    <Box width={'100%'}>
                      <SelectCard
                        summary={'フチカラー'}
                        selectedLabel={edgeColorLabel}
                        selectedColor={embroidery.edgeColor?.color}
                        objects={edgeColors}
                        isError={embroidery.edgeColor?.value === 'unselected'}
                        defaultExpanded={false}
                        index={i}
                        handleChange={handle.edgeColor}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )
          })}
        </>
      )}
    </TabPanel>
  )
}
